import type React from "react";
import {useCallback} from "react";

import {faShareNodes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import CustomButton from "./CustomButton";

interface ShareButtonProps {
  url?: string;
  className?: string;
  stopPropagation?: boolean;
}

/**
 * A reusable share button component that shares the provided URL
 * or the current URL if none is provided.
 */
const ShareButton: React.FC<ShareButtonProps> = ({
  url,
  className = "p-2 rounded-full hover:bg-black hover:bg-opacity-70 transition-colors",
  stopPropagation = false,
}) => {
  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      if (stopPropagation) {
        e.stopPropagation();
      }

      const shareUrl = url || window.location.href;

      if (navigator.share) {
        navigator
          .share({
            url: shareUrl,
          })
          .catch((error) => console.error("Error sharing:", error));
      } else {
        navigator.clipboard
          .writeText(shareUrl)
          .then(() => {
            alert("Link copied to clipboard!");
          })
          .catch((err) => {
            console.error("Failed to copy link: ", err);
          });
      }
    },
    [url, stopPropagation],
  );

  return (
    <CustomButton
      onClick={handleShare}
      aria-label="Share"
      className={className}
    >
      <FontAwesomeIcon
        icon={faShareNodes}
        size="xl"
        className="text-white p-2 -m-2 rounded-full bg-gradient-to-br from-blue-600 via-blue-500 to-black"
      />
    </CustomButton>
  );
};

export default ShareButton;
